import { useCallback, useState } from 'react'
import { API_URL } from '../api/config'

export interface ImportResponse {
  message: string;
  totalImportados: number;
  totalAtualizados: number;
  store: string;
  rejeitadosPorStatus: {
    total: number;
    exemplos: Array<{ usuario: string; status: string }>;
  };
  atualizados: {
    total: number;
    exemplos: Array<{ usuario: string; detalhes: string }>;
  };
  erros: Array<{ usuario: string; regra: string }>;
}

export interface CSVPreview {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

export type ImportState = 'idle' | 'preview' | 'uploading' | 'success' | 'error';

export const useCSVImport = () => {
  const [state, setState] = useState<ImportState>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CSVPreview | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  const validateFile = useCallback((file: File): string | null => {
    if (!file) return 'Arquivo é obrigatório';
    if (!file.name.toLowerCase().endsWith('.csv')) return 'Apenas arquivos CSV são aceitos';
    if (file.size > 10 * 1024 * 1024) return 'Arquivo deve ter no máximo 10MB';
    return null;
  }, []);

  const parseCSV = useCallback((text: string): CSVPreview => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0]?.split(',').map(h => h.trim()) || [];
    const rows = lines.slice(1, 6).map(line => 
      line.split(',').map(cell => cell.trim())
    );
    
    return {
      headers,
      rows,
      totalRows: lines.length - 1
    };
  }, []);

  const loadPreview = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setState('error');
      return;
    }

    try {
      setState('preview');
      setFile(file);
      setError(null);

      const text = await file.text();
      const previewData = parseCSV(text);
      setPreview(previewData);
    } catch (err) {
      setError('Erro ao ler o arquivo CSV');
      setState('error');
    }
  }, [validateFile, parseCSV]);

  const importCSV = useCallback(async (store: string = 'italia-consulado') => {
    if (!file) {
      setError('Nenhum arquivo selecionado');
      setState('error');
      return;
    }

    try {
      setState('uploading');
      setProgress(0);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('store', store);

      const xhr = new XMLHttpRequest();
      
      return new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            setProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response: ImportResponse = JSON.parse(xhr.responseText);
              setResult(response);
              setState('success');
              resolve();
            } catch (err) {
              setError('Erro ao processar resposta da API');
              setState('error');
              reject(err);
            }
          } else {
            setError(`Erro na importação: ${xhr.statusText}`);
            setState('error');
            reject(new Error(xhr.statusText));
          }
        });

        xhr.addEventListener('error', () => {
          setError('Erro de conexão com o servidor');
          setState('error');
          reject(new Error('Network error'));
        });

        xhr.open('POST', `${API_URL}users/import-csv`);
        xhr.send(formData);
      });
    } catch (err) {
      setError('Erro inesperado durante a importação');
      setState('error');
    }
  }, [file]);

  const reset = useCallback(() => {
    setState('idle');
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setProgress(0);
  }, []);

  const generateErrorReport = useCallback((): string => {
    if (!result) return '';

    let report = `RELATÓRIO DE IMPORTAÇÃO - ${new Date().toLocaleString('pt-BR')}\n`;
    report += `=================================================\n\n`;
    report += `Loja/Consulado: ${result.store}\n`;
    report += `Total Importados: ${result.totalImportados}\n`;
    report += `Total Atualizados: ${result.totalAtualizados}\n\n`;

    if (result.rejeitadosPorStatus.total > 0) {
      report += `REJEITADOS POR STATUS (${result.rejeitadosPorStatus.total}):\n`;
      result.rejeitadosPorStatus.exemplos.forEach(item => {
        report += `- ${item.usuario}: ${item.status}\n`;
      });
      report += '\n';
    }

    if (result.atualizados.total > 0) {
      report += `ATUALIZADOS (${result.atualizados.total}):\n`;
      result.atualizados.exemplos.forEach(item => {
        report += `- ${item.usuario}: ${item.detalhes}\n`;
      });
      report += '\n';
    }

    if (result.erros.length > 0) {
      report += `ERROS (${result.erros.length}):\n`;
      result.erros.forEach(erro => {
        report += `- ${erro.usuario}: ${erro.regra}\n`;
      });
    }

    return report;
  }, [result]);

  return {
    state,
    file,
    preview,
    result,
    error,
    progress,
    loadPreview,
    importCSV,
    reset,
    generateErrorReport
  };
}; 